// src/store/conges.js
import { defineStore } from 'pinia'
import api from '@/services/api'

export const useCongeStore = defineStore('conges', {
  state: () => ({
    conges: [],
    loading: false,
    error: null,
    currentConge: null,
    pagination: {
      page: 1,
      limit: 10,
      totalItems: 0,
      totalPages: 0
    },
    filters: {
      agentId: null,
      typeConge: null,
      statut: null,
      dateDebut: null,
      dateFin: null
    },
    typesConge: [],
    quotas: [],
    calendarConges: [],
    directions: [],
    joursNonOuvrables: []
  }),
  
  getters: {
    getCongeById: (state) => (id) => {
      return state.conges.find(conge => conge.id === parseInt(id))
    },
    
    getCongesByStatus: (state) => (statut) => {
      return state.conges.filter(conge => conge.statut === statut)
    },
    
    getCongesByAgent: (state) => (agentId) => {
      return state.conges.filter(conge => conge.agentId === parseInt(agentId))
    },
    
    getTypeCongeById: (state) => (id) => {
      return state.typesConge.find(type => type.id === parseInt(id))
    },
    
    getQuotaByAgentAndType: (state) => (agentId, typeCongeId) => {
      return state.quotas.find(quota => 
        quota.agentId === parseInt(agentId) && 
        quota.typeCongeId === parseInt(typeCongeId)
      )
    }
  },
  
  actions: {
    async fetchConges() {
      this.loading = true
      try {
        const params = {
          ...this.filters,
          page: this.pagination.page,
          limit: this.pagination.limit,
          withApprobations: true,
          withQuotas: true
        }
        const response = await api.getConges(params)
        this.conges = response.data.data
        this.pagination = response.data.pagination
        this.error = null
      } catch (error) {
        console.error('Erreur lors de la récupération des congés:', error)
        this.error = error.response?.data?.message || 'Erreur lors de la récupération des congés'
      } finally {
        this.loading = false
      }
    },
    
    async fetchCongeById(id) {
      this.loading = true
      try {
        const response = await api.getCongeById(id, { withApprobations: true })
        this.currentConge = response.data.data
        this.error = null
        return this.currentConge
      } catch (error) {
        console.error(`Erreur lors de la récupération du congé ${id}:`, error)
        this.error = error.response?.data?.message || `Erreur lors de la récupération du congé ${id}`
        return null
      } finally {
        this.loading = false
      }
    },
    
    async createConge(congeData) {
      this.loading = true
      try {
        const response = await api.createConge(congeData)
        // Ajouter le nouveau congé à la liste si nécessaire
        if (this.conges.length > 0) {
          this.conges.unshift(response.data.data)
        }
        this.error = null
        return response.data.data
      } catch (error) {
        console.error('Erreur lors de la création du congé:', error)
        this.error = error.response?.data?.message || 'Erreur lors de la création du congé'
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async updateConge(id, congeData) {
      this.loading = true
      try {
        const response = await api.updateConge(id, congeData)
        // Mettre à jour le congé dans la liste si nécessaire
        const index = this.conges.findIndex(conge => conge.id === parseInt(id))
        if (index !== -1) {
          this.conges[index] = response.data.data
        }
        // Mettre à jour le congé courant si c'est celui qui est modifié
        if (this.currentConge && this.currentConge.id === parseInt(id)) {
          this.currentConge = response.data.data
        }
        this.error = null
        return response.data.data
      } catch (error) {
        console.error(`Erreur lors de la mise à jour du congé ${id}:`, error)
        this.error = error.response?.data?.message || `Erreur lors de la mise à jour du congé ${id}`
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async deleteConge(id) {
      this.loading = true
      try {
        await api.deleteConge(id)
        // Supprimer le congé de la liste
        this.conges = this.conges.filter(conge => conge.id !== parseInt(id))
        // Réinitialiser le congé courant si c'est celui qui est supprimé
        if (this.currentConge && this.currentConge.id === parseInt(id)) {
          this.currentConge = null
        }
        this.error = null
        return true
      } catch (error) {
        console.error(`Erreur lors de la suppression du congé ${id}:`, error)
        this.error = error.response?.data?.message || `Erreur lors de la suppression du congé ${id}`
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async changeCongeStatus(id, statusData) {
      this.loading = true
      try {
        const response = await api.changeCongeStatus(id, statusData)
        // Mettre à jour le statut du congé dans la liste
        const index = this.conges.findIndex(conge => conge.id === parseInt(id))
        if (index !== -1) {
          this.conges[index].statut = statusData.statut
          this.conges[index].approbations = response.data.data.approbations
        }
        // Mettre à jour le congé courant si c'est celui qui est modifié
        if (this.currentConge && this.currentConge.id === parseInt(id)) {
          this.currentConge.statut = statusData.statut
          this.currentConge.approbations = response.data.data.approbations
        }
        this.error = null
        return response.data.data
      } catch (error) {
        console.error(`Erreur lors du changement de statut du congé ${id}:`, error)
        this.error = error.response?.data?.message || `Erreur lors du changement de statut du congé ${id}`
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async fetchTypesConge() {
      this.loading = true
      try {
        const response = await api.getCongesTypes()
        this.typesConge = response.data.data
        this.error = null

        return response.data.data
      } catch (error) {
        console.error('Erreur lors de la récupération des types de congé:', error)
        this.error = error.response?.data?.message || 'Erreur lors de la récupération des types de congé'
      } finally {
        this.loading = false
      }
    },
    
    async fetchQuotas(params = {}) {
      this.loading = true
      try {
        const response = await api.getCongesQuotas(params)
        this.quotas = response.data.data
        this.error = null
      } catch (error) {
        console.error('Erreur lors de la récupération des quotas de congé:', error)
        this.error = error.response?.data?.message || 'Erreur lors de la récupération des quotas de congé'
      } finally {
        this.loading = false
      }
    },
    
    setFilters(filters) {
      this.filters = { ...this.filters, ...filters }
    },
    
    resetFilters() {
      this.filters = {
        agentId: null,
        typeConge: null,
        statut: null,
        dateDebut: null,
        dateFin: null
      }
    },
    
    setPage(page) {
      this.pagination.page = page
    },
    
    setLimit(limit) {
      this.pagination.limit = limit
      this.pagination.page = 1 // Réinitialiser à la première page lors du changement de limite
    },
    
    // Méthodes pour le calendrier des congés
    async fetchCongesForCalendar(params = {}) {
      this.loading = true
      this.error = null
      
      try {
        const response = await api.getCongesForCalendar(params)
        this.calendarConges = response.data.items || []
        return response.data
      } catch (error) {
        console.error('Erreur lors du chargement des congés pour le calendrier:', error)
        this.error = error.message || 'Erreur lors du chargement des congés'
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async fetchDirections() {
      try {
        const response = await api.getDirections()
        this.directions = response.data.items || []
        return response
      } catch (error) {
        console.error('Erreur lors du chargement des directions:', error)
        throw error
      }
    },
    
    async fetchJoursNonOuvrables(annee) {
      try {
        const response = await api.getJoursNonOuvrables({ annee })
        this.joursNonOuvrables = response.data.data || []
        return response
      } catch (error) {
        console.error('Erreur lors du chargement des jours non ouvrables:', error)
        throw error
      }
    }
  }
})
